// Copyright (c) 2026 embyt GmbH. All rights reserved.
// Author: Roman Morawek

// authentication workflows of the cloud build: login/logout, sign-up,
// password reset, password change, and TOTP MFA handling

import { put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { Base64 } from "js-base64";
import {
  ConfirmSignInOutput,
  FetchMFAPreferenceOutput,
  FetchUserAttributesOutput,
  GetCurrentUserOutput,
  ResendSignUpCodeOutput,
  ResetPasswordOutput,
  SetUpTOTPOutput,
  SignInOutput,
  SignUpOutput,
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  fetchMFAPreference,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  setUpTOTP,
  signIn,
  signOut,
  signUp,
  updateMFAPreference,
  updatePassword,
  verifyTOTPSetup,
} from "aws-amplify/auth";

import {
  ActionType,
  AuthFlow,
  IAction,
  IActionTypeAuthFail,
  IActionTypeChangeAwsUser,
  IActionTypeForgotPassword,
  IActionTypeLogin,
  IActionTypeLoginResult,
  IActionTypeMfaSetup,
  IActionTypeSignUp,
  IActionTypeSignUpResult,
  raiseAction,
} from "actions";
import { RootPath, UserStateAWSError } from "types";
import { IState } from "states/states";
import { isSet } from "shared/utils/utils";
import { raiseAuthError } from "./apisagas";

const getState = (state: IState) => state;

// register the watchers of the auth workflows
// login/logout/auto-login are registered in ApiSagas.initSagas via the CloudSagas delegates
export function* initAuthSagas() {
  yield takeLatest(ActionType[ActionType.ACT_SIGNUP_REQ], doSignUp);
  yield takeLatest(ActionType[ActionType.ACT_SIGNUP_RESEND_REQ], doSignUpResend);
  yield takeLatest(ActionType[ActionType.ACT_SIGNUP_CONFIRM_REQ], doSignUpConfirm);
  yield takeLatest(ActionType[ActionType.ACT_FORGOT_PASSWORD_CODE_REQ], reqPasswordCode);
  yield takeLatest(ActionType[ActionType.ACT_FORGOT_PASSWORD_CHANGE_REQ], reqPasswordChange);
  yield takeEvery(ActionType[ActionType.ACT_CHANGE_AWSUSER_REQ], changeAwsUser);
  yield takeLatest(ActionType[ActionType.ACT_MFA_CONFIRM_REQ], confirmMfaCode);
  yield takeLatest(ActionType[ActionType.ACT_MFA_STATUS_REQ], getMfaStatus);
  yield takeLatest(ActionType[ActionType.ACT_MFA_SETUP_REQ], setupMfa);
  yield takeLatest(ActionType[ActionType.ACT_MFA_VERIFY_REQ], verifyMfaSetup);
  yield takeLatest(ActionType[ActionType.ACT_MFA_DISABLE_REQ], disableMfa);
  yield takeEvery(ActionType[ActionType.ACT_LOGIN_CREATE_RFID_USER], createRfidUser);
}

export function* doAutoLogin() {
  // check for auto-login as URL argument
  let autoLogin: IActionTypeLogin | undefined;
  if (new URL(window.document.URL).searchParams.has("login")) {
    const auth = new URL(window.document.URL).searchParams.get("login")!;
    autoLogin = JSON.parse(Base64.decode(auth)) as IActionTypeLogin;
  }
  // routerState may not be available at this point, so we use the URL path
  const isRfidUser = window.location.pathname.startsWith(`/${RootPath.RFID}`);
  // check AWS cached login
  let userData: IActionTypeLoginResult | undefined;
  try {
    const curUser = (yield getCurrentUser()) as GetCurrentUserOutput;
    if (isRfidUser) {
      userData = {
        // we use the rfid as userId, not the AWS userId
        userId: curUser.username,
        isRfidUser,
      };
    } else {
      const curAttributes = (yield fetchUserAttributes()) as FetchUserAttributesOutput;
      userData = {
        userId: curUser.userId,
        email: curAttributes?.email,
        realName: curAttributes?.name,
        isRfidUser,
      };
    }
  } catch (error) {
    // getCurrentUser throws if not logged in
    // it also throws with "PasswordResetRequiredException", which is fine
    if ((error as Error).name !== "UserUnAuthenticatedException") {
      console.log("auto-login failed", error);
    }
  }

  // AWS cached login has preference to URL login, when user matches
  if (userData && (!autoLogin || userData.email === autoLogin.username)) {
    console.log("resuming login session");
    yield put(raiseAction(ActionType.ACT_LOGIN_SUCC, userData));
  }
  // alternatively, we may auto-login using URL
  else if (autoLogin) {
    yield put(raiseAction(ActionType.ACT_LOGIN_REQ, autoLogin));
  } else {
    yield put(raiseAction(ActionType.ACT_GOTO_LOGIN));
  }
}

export function* doLogin(action: IAction<IActionTypeLogin>) {
  // routerState may not be available at this point, so we use the URL path
  const isRfidUser = window.location.pathname.startsWith(`/${RootPath.RFID}`);
  const username = isRfidUser ? action.payload.username.toLowerCase() : action.payload.username;
  try {
    // login workflow of rfid user does not have much in common to the AWS login
    if (isRfidUser) {
      const result = (yield signIn({
        username,
        options: { authFlowType: "CUSTOM_WITHOUT_SRP" },
      })) as SignInOutput | undefined;
      if (result?.isSignedIn && result.nextStep.signInStep === "DONE") {
        const userData: IActionTypeLoginResult = {
          // we use the rfid as userId, not the AWS userId
          userId: username,
          isRfidUser,
        };
        yield put(raiseAction(ActionType.ACT_LOGIN_SUCC, userData));
      } else {
        console.error("unexpected rfid auth response", result);
        yield put(
          raiseAction(ActionType.ACT_AUTH_FAIL, {
            flow: AuthFlow.LOGIN,
            errorText: "unexpected auth response",
          } as IActionTypeAuthFail),
        );
      }
    }
    // AWS loging workflow
    else {
      const result = (yield signIn({
        username,
        password: action.payload.password,
      })) as SignInOutput | undefined;
      if (result?.isSignedIn && result.nextStep.signInStep === "DONE") {
        const userAttributes = (yield fetchUserAttributes()) as FetchUserAttributesOutput;
        const userData: IActionTypeLoginResult = {
          userId: userAttributes.sub!,
          isRfidUser,
          realName: userAttributes.name,
          email: userAttributes.email,
        };
        yield put(raiseAction(ActionType.ACT_LOGIN_SUCC, userData));
      } else if (result?.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        // set flag to invoke password change form
        yield put(raiseAction(ActionType.ACT_FORCE_PWD_CHANGE, username));
      } else if (result?.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
        // user has TOTP MFA enabled -> switch to the code entry form
        yield put(raiseAction(ActionType.ACT_MFA_CODE_REQUIRED, username));
      } else if (result?.nextStep.signInStep === "CONFIRM_SIGN_UP") {
        // link to Code-Entry-Screen to show that the sign-up confirmation is still pending
        const payload: IActionTypeSignUp = {
          username,
          password: action.payload.password,
          keepErrorState: true,
        };
        yield put(raiseAction(ActionType.ACT_SIGNUP_CONFIRM, payload));
      } else if (result?.isSignedIn) {
        console.error("unexpected login step", result);
        yield put(
          raiseAction(ActionType.ACT_AUTH_FAIL, {
            flow: AuthFlow.LOGIN,
            errorText: result.nextStep.signInStep,
          } as IActionTypeAuthFail),
        );
      } else {
        console.error("unexpected auth response", result);
        yield put(
          raiseAction(ActionType.ACT_AUTH_FAIL, {
            flow: AuthFlow.LOGIN,
            errorText: "unexpected auth response",
          } as IActionTypeAuthFail),
        );
      }
    }
  } catch (error) {
    if (["UserNotFoundException", "NotAuthorizedException"].includes((error as Error).name)) {
      if (isRfidUser) {
        // catch error when an RFID user newly gets created
        yield put(raiseAction(ActionType.ACT_LOGIN_CREATE_RFID_USER, username));
      } else {
        yield put(
          raiseAction(ActionType.ACT_AUTH_FAIL, {
            flow: AuthFlow.LOGIN,
            awsError: UserStateAWSError.LOGIN_FAILED,
          } as IActionTypeAuthFail),
        );
      }
    } else if ((error as Error).name === "UserAlreadyAuthenticatedException") {
      console.log("already logged in, but not valid. logging out and retrying.");
      yield signOut();
      // retry login
      yield put(raiseAction(ActionType.ACT_LOGIN_REQ, action.payload));
    } else {
      yield raiseAuthError(AuthFlow.LOGIN, error);
    }
  }
}

// second login step if the user has TOTP MFA enabled
function* confirmMfaCode(action: IAction<string>) {
  try {
    const result = (yield confirmSignIn({
      challengeResponse: action.payload,
    })) as ConfirmSignInOutput;
    if (result.isSignedIn && result.nextStep.signInStep === "DONE") {
      // finish login sequence
      const userAttributes = (yield fetchUserAttributes()) as FetchUserAttributesOutput;
      const userData: IActionTypeLoginResult = {
        userId: userAttributes.sub!,
        isRfidUser: false,
        realName: userAttributes.name,
        email: userAttributes.email,
      };
      yield put(raiseAction(ActionType.ACT_LOGIN_SUCC, userData));
    } else {
      console.error("unexpected MFA confirm result", result);
      yield put(
        raiseAction(ActionType.ACT_AUTH_FAIL, {
          flow: AuthFlow.LOGIN,
          errorText: result.nextStep.signInStep,
        } as IActionTypeAuthFail),
      );
    }
  } catch (error) {
    // e.g. code mismatch or expired sign-in session;
    // the user can retry the code or cancel back to the login form
    yield raiseAuthError(AuthFlow.LOGIN, error);
  }
}

export function* doLogout(_action: IAction<undefined>) {
  // site connection active?
  const state = (yield select(getState)) as IState;
  if (isSet(state.cached.selectedSite)) {
    // we need to disconnect before logout to avoid errors at unsubscribing live data
    yield put(raiseAction(ActionType.ACT_DISCONNECT_SITE));
  }
  try {
    // perform logout
    yield signOut();
    yield put(raiseAction(ActionType.ACT_LOGOUT_SUCC));
  } catch (error) {
    yield put(
      raiseAction(ActionType.ACT_AUTH_FAIL, {
        flow: AuthFlow.LOGOUT,
        awsError: UserStateAWSError.LOGOUT_FAILED,
      } as IActionTypeAuthFail),
    );
  }
}

function* doSignUp(action: IAction<IActionTypeSignUp>) {
  try {
    // AWS user creation workflow
    const result = (yield signUp({
      username: action.payload.username,
      password: action.payload.password ?? "",
      options: {
        userAttributes: {
          name: action.payload.realName,
          email: action.payload.username,
          // we need to send an empty string if the user signs up by himself
          // as this is only allowed for prepared users (company already set) or pre-defined domains
          "custom:company": "",
        },
      },
    })) as SignUpOutput | undefined;

    if (result?.isSignUpComplete && result.nextStep.signUpStep === "DONE") {
      const userAttributes = (yield fetchUserAttributes()) as FetchUserAttributesOutput;
      const userData: IActionTypeSignUpResult = {
        userId: result.userId,
        realName: userAttributes.name,
        destination: userAttributes.email,
        confirmed: true,
      };
      yield put(raiseAction(ActionType.ACT_SIGNUP_SUCC, userData));

      // try to login now!
      const loginData: IActionTypeLogin = {
        username: action.payload.username,
        password: action.payload.password ?? "",
      };
      yield put(raiseAction(ActionType.ACT_LOGIN_REQ, loginData));
    } else if (result?.nextStep.signUpStep === "CONFIRM_SIGN_UP") {
      // set flag to invoke page to show that the sign-up confirmation is still pending!
      yield put(
        raiseAction(ActionType.ACT_AUTH_FAIL, {
          flow: AuthFlow.COGNITO,
          awsError: UserStateAWSError.CONFIRMATION_PENDING,
        } as IActionTypeAuthFail),
      );
      yield put(
        raiseAction(ActionType.ACT_SIGNUP_CONFIRM, {
          username: action.payload.username,
          keepErrorState: true,
        } as IActionTypeSignUp),
      );
    } else if (result?.userId) {
      console.error("unexpected signup step", result);
      yield put(
        raiseAction(ActionType.ACT_AUTH_FAIL, {
          flow: AuthFlow.COGNITO,
          errorText: result.nextStep.signUpStep,
        } as IActionTypeAuthFail),
      );
    } else {
      console.error("unexpected auth response", result);
      yield put(
        raiseAction(ActionType.ACT_AUTH_FAIL, {
          flow: AuthFlow.COGNITO,
          errorText: "unexpected sign-up response",
        } as IActionTypeAuthFail),
      );
    }
  } catch (error) {
    if ((error as Error).name === "UsernameExistsException") {
      // try to login
      const loginData: IActionTypeLogin = {
        username: action.payload.username,
        password: action.payload.password ?? "",
      };
      yield put(raiseAction(ActionType.ACT_LOGIN_REQ, loginData));
    } else if (
      (error as Error).name === "UserLambdaValidationException" &&
      (error as Error).message.includes("unsupported company")
    ) {
      yield put(
        raiseAction(ActionType.ACT_AUTH_FAIL, {
          flow: AuthFlow.COGNITO,
          awsError: UserStateAWSError.SIGNUP_UNSUPPORTED_COMPANY,
        } as IActionTypeAuthFail),
      );
    } else {
      yield raiseAuthError(AuthFlow.COGNITO, error);
    }
  }
}

function* doSignUpResend(action: IAction<IActionTypeSignUp>) {
  const username = action.payload.username;
  try {
    const result = (yield resendSignUpCode({
      username,
    })) as ResendSignUpCodeOutput | undefined;
    const userData: IActionTypeSignUpResult = {
      destination: result?.destination ?? "",
      attribute: result?.attributeName ?? "email",
      medium: result?.deliveryMedium ?? "EMAIL",
      confirmed: false,
    };
    yield put(raiseAction(ActionType.ACT_SIGNUP_SUCC, userData));
  } catch (error) {
    yield raiseAuthError(AuthFlow.COGNITO, error);
  }
}

function* doSignUpConfirm(action: IAction<IActionTypeSignUp>) {
  const username = action.payload.username;
  try {
    const result = (yield confirmSignUp({
      username,
      confirmationCode: action.payload.code ?? "",
    })) as ResendSignUpCodeOutput | undefined;
    const userData: IActionTypeSignUpResult = {
      destination: result?.destination ?? "",
      attribute: result?.attributeName ?? "email",
      medium: result?.deliveryMedium ?? "EMAIL",
      confirmed: true,
    };
    yield put(raiseAction(ActionType.ACT_SIGNUP_SUCC, userData));

    // try to login now!
    const loginData: IActionTypeLogin = {
      username: action.payload.username,
      password: action.payload.password ?? "",
    };
    yield put(raiseAction(ActionType.ACT_LOGIN_REQ, loginData));
  } catch (error) {
    yield raiseAuthError(AuthFlow.COGNITO, error, {
      NotAuthorizedException: UserStateAWSError.ALREADY_CONFIRMED,
    });
  }
}

function* reqPasswordCode(action: IAction<IActionTypeForgotPassword>) {
  const payload = action.payload;
  try {
    const result = (yield resetPassword({ username: payload.username })) as ResetPasswordOutput;
    yield put(raiseAction(ActionType.ACT_FORGOT_PASSWORD_CODE_SUCC, result));
  } catch (error) {
    yield raiseAuthError(AuthFlow.COGNITO, error);
  }
}

function* reqPasswordChange(action: IAction<IActionTypeForgotPassword>) {
  const payload = action.payload;
  try {
    const result = (yield confirmResetPassword({
      username: payload.username,
      newPassword: payload.newPassword,
      confirmationCode: payload.confirmationCode,
    })) as string;
    yield put(raiseAction(ActionType.ACT_FORGOT_PASSWORD_CHANGE_SUCC, result));
  } catch (error) {
    yield raiseAuthError(AuthFlow.COGNITO, error);
  }
}

function* changeAwsUser(action: IAction<IActionTypeChangeAwsUser>) {
  // this can only happen in cloud usage
  const isPasswordChange = action.payload.realName === undefined;
  try {
    if (isPasswordChange) {
      yield updatePassword({
        oldPassword: action.payload.oldPassword,
        newPassword: action.payload.newPassword,
      });
      yield put(raiseAction(ActionType.ACT_CHANGE_AWSUSER_SUCC));
    } else {
      // CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED
      const result = (yield confirmSignIn({
        challengeResponse: action.payload.newPassword,
        options: {
          userAttributes: {
            name: action.payload.realName,
          },
        },
      })) as ConfirmSignInOutput;
      if (result.isSignedIn && result.nextStep.signInStep === "DONE") {
        // finish login sequence
        const userAttributes = (yield fetchUserAttributes()) as FetchUserAttributesOutput;
        const userData: IActionTypeLoginResult = {
          userId: userAttributes.sub!,
          isRfidUser: false,
          realName: userAttributes.name,
          email: userAttributes.email,
        };
        yield put(raiseAction(ActionType.ACT_LOGIN_SUCC, userData));
      } else {
        console.error("unexpected confirmSignin result", result);
        yield put(
          raiseAction(ActionType.ACT_AUTH_FAIL, {
            flow: AuthFlow.USER_CHANGE,
            errorText: "unexpected auth response",
          } as IActionTypeAuthFail),
        );
      }
    }
  } catch (error) {
    yield raiseAuthError(AuthFlow.USER_CHANGE, error);
  }
}

function* getMfaStatus() {
  try {
    const result = (yield fetchMFAPreference()) as FetchMFAPreferenceOutput;
    yield put(
      raiseAction(ActionType.ACT_MFA_STATUS_SUCC, result.enabled?.includes("TOTP") === true),
    );
  } catch (error) {
    yield raiseAuthError(AuthFlow.MFA_SETTINGS, error);
  }
}

function* setupMfa() {
  const state = (yield select(getState)) as IState;
  try {
    const totpSetup = (yield setUpTOTP()) as SetUpTOTPOutput;
    const setupDetails: IActionTypeMfaSetup = {
      setupUri: totpSetup.getSetupUri("enline cloud", state.site.user?.email).toString(),
      secret: totpSetup.sharedSecret,
    };
    yield put(raiseAction(ActionType.ACT_MFA_SETUP_SUCC, setupDetails));
  } catch (error) {
    yield raiseAuthError(AuthFlow.MFA_SETTINGS, error);
  }
}

function* verifyMfaSetup(action: IAction<string>) {
  try {
    yield verifyTOTPSetup({ code: action.payload });
    yield updateMFAPreference({ totp: "PREFERRED" });
    yield put(raiseAction(ActionType.ACT_MFA_STATUS_SUCC, true));
  } catch (error) {
    yield raiseAuthError(AuthFlow.MFA_SETTINGS, error);
  }
}

function* disableMfa() {
  try {
    yield updateMFAPreference({ totp: "DISABLED" });
    yield put(raiseAction(ActionType.ACT_MFA_STATUS_SUCC, false));
  } catch (error) {
    yield raiseAuthError(AuthFlow.MFA_SETTINGS, error);
  }
}

function createRfidEmail(rfid: string) {
  return `rfid-${rfid}@embyt.com`;
}

function* createRfidUser(action: IAction<string>) {
  const rfid = action.payload;
  const password = "<no-rfid-password>"; // not needed for rfid users
  const email = createRfidEmail(rfid);
  try {
    const result = (yield signUp({
      username: rfid,
      password,
      options: {
        userAttributes: {
          name: rfid,
          email,
        },
      },
    })) as SignUpOutput;
    console.log("new user signup", rfid, result);
    // after user creation, trigger login
    const loginData: IActionTypeLogin = {
      username: rfid,
      password,
    };
    yield put(raiseAction(ActionType.ACT_LOGIN_REQ, loginData));
  } catch (error) {
    yield raiseAuthError(AuthFlow.LOGIN, error);
  }
}
